import { QueryKey, useIsMutating, useQueryClient } from '@tanstack/react-query';

import { archiveJob, restoreJob } from 'api/jobs';
import { Job, JobArchiveOperation, JobArchiveResponse, JobArchiveState, JobResponse, JobType } from 'interfaces/job';
import { getJobQueryKey } from 'utils/queryHooks/jobs/useJob';
import {
  jobExperimentResultDetailQueryKey,
  jobExperimentResultSummariesQueryKey,
} from 'utils/queryHooks/jobs/useJobExperimentResults';
import { jobsQueryKey } from 'utils/queryHooks/jobs/useJobs';
import { jobsFilteredIdsQueryKey } from 'utils/queryHooks/useJobsFilteredIds';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { invalidateSlideVisibility } from 'utils/queryHooks/invalidateSlideVisibility';

interface JobArchiveMutationVariables {
  jobId: string;
  operation: JobArchiveOperation;
}

interface JobCollectionSnapshot {
  queryKey: QueryKey;
  job: Job;
  index: number;
}

interface FilteredIdsSnapshot {
  queryKey: QueryKey;
  index: number;
}

interface JobArchiveMutationContext {
  previousJob?: Job;
  jobCollectionSnapshots: JobCollectionSnapshot[];
  filteredIdsSnapshots: FilteredIdsSnapshot[];
}

export const jobArchiveMutationKey = ['jobArchive'] as const;

const queryBelongsToLab = (queryKey: readonly unknown[], labId: string) => {
  const params = queryKey[1];
  return typeof params === 'object' && params !== null && 'labId' in params && params.labId === labId;
};

const getQueryArchiveState = (queryKey: readonly unknown[]): JobArchiveState => {
  const params = queryKey[1];
  if (typeof params !== 'object' || params === null || !('filters' in params)) {
    return JobArchiveState.Active;
  }

  const filters = params.filters;
  if (typeof filters !== 'object' || filters === null || !('archiveState' in filters)) {
    return JobArchiveState.Active;
  }

  return (filters.archiveState as JobArchiveState | undefined) || JobArchiveState.Active;
};

const isSourceArchiveScope = (archiveState: JobArchiveState, operation: JobArchiveOperation) =>
  (operation === 'archive' && archiveState === JobArchiveState.Active) ||
  (operation === 'restore' && archiveState === JobArchiveState.Archived);

const updateJobCollection = (
  current: JobResponse | undefined,
  { jobId, operation }: JobArchiveMutationVariables,
  archiveMetadata: Pick<Job, 'deletedAt' | 'deletedBy'>,
  archiveState: JobArchiveState
) => {
  if (!current) {
    return current;
  }

  const jobIndex = current.jobs.findIndex((job) => job.id === jobId);
  if (jobIndex < 0) {
    return current;
  }

  if (isSourceArchiveScope(archiveState, operation)) {
    return {
      ...current,
      jobs: current.jobs.filter((job) => job.id !== jobId),
      totalJobs: Math.max(0, current.totalJobs - 1),
    };
  }

  return {
    ...current,
    jobs: current.jobs.map((job) => (job.id === jobId ? { ...job, ...archiveMetadata } : job)),
  };
};

const restoreJobCollection = (current: JobResponse | undefined, snapshot: JobCollectionSnapshot) => {
  if (!current) {
    return current;
  }

  if (current.jobs.some((job) => job.id === snapshot.job.id)) {
    return {
      ...current,
      jobs: current.jobs.map((job) => (job.id === snapshot.job.id ? snapshot.job : job)),
    };
  }

  const jobs = [...current.jobs];
  jobs.splice(Math.min(snapshot.index, jobs.length), 0, snapshot.job);
  return { ...current, jobs, totalJobs: current.totalJobs + 1 };
};

const restoreFilteredIds = (current: string[] | undefined, snapshot: FilteredIdsSnapshot, jobId: string) => {
  if (!current || current.includes(jobId)) {
    return current;
  }

  const ids = [...current];
  ids.splice(Math.min(snapshot.index, ids.length), 0, jobId);
  return ids;
};

const getPendingOperation = (queryClient: ReturnType<typeof useQueryClient>, jobId: string) => {
  const pendingMutation = queryClient
    .getMutationCache()
    .findAll({ mutationKey: jobArchiveMutationKey })
    .find(
      (mutation) =>
        mutation.state.status === 'loading' &&
        (mutation.state.variables as unknown as JobArchiveMutationVariables | undefined)?.jobId === jobId
    );
  return (pendingMutation?.state.variables as unknown as JobArchiveMutationVariables | undefined)?.operation ?? null;
};

const isIngestFamilyJob = (job: Job) => job.type === JobType.Ingest || job.type === JobType.BatchIngest;

export const usePendingJobArchiveOperation = (jobId: string) => {
  const queryClient = useQueryClient();
  useIsMutating({ mutationKey: jobArchiveMutationKey });
  return getPendingOperation(queryClient, jobId);
};

export const useJobArchiveMutation = () => {
  const { labId } = useCurrentLabId();
  const queryClient = useQueryClient();

  const mutation = useMutationWithErrorSnackbar<
    JobArchiveResponse,
    JobArchiveMutationVariables,
    JobArchiveMutationContext
  >({
    mutationKey: jobArchiveMutationKey,
    mutationDescription: 'update job archive state',
    mutationFn: ({ jobId, operation }) =>
      operation === 'archive' ? archiveJob(labId, jobId) : restoreJob(labId, jobId),
    onMutate: async (variables) => {
      const jobsQueryPredicate = ({ queryKey }: { queryKey: QueryKey }) =>
        queryKey[0] === jobsQueryKey && queryBelongsToLab(queryKey, labId);
      const filteredIdsQueryPredicate = ({ queryKey }: { queryKey: QueryKey }) =>
        queryKey[0] === jobsFilteredIdsQueryKey && queryBelongsToLab(queryKey, labId);

      await Promise.all([
        queryClient.cancelQueries({ predicate: jobsQueryPredicate }),
        queryClient.cancelQueries({ predicate: filteredIdsQueryPredicate }),
        queryClient.cancelQueries({ queryKey: getJobQueryKey(variables.jobId) }),
      ]);

      const previousJob = queryClient.getQueryData<Job>(getJobQueryKey(variables.jobId));
      const jobQueries = queryClient.getQueriesData<JobResponse>({ predicate: jobsQueryPredicate });
      const filteredIdsQueries = queryClient.getQueriesData<string[]>({ predicate: filteredIdsQueryPredicate });
      const jobCollectionSnapshots = jobQueries.flatMap(([queryKey, response]) => {
        const index = response?.jobs.findIndex((job) => job.id === variables.jobId) ?? -1;
        return index >= 0 && response ? [{ queryKey, job: response.jobs[index], index }] : [];
      });
      const filteredIdsSnapshots = filteredIdsQueries.flatMap(([queryKey, ids]) => {
        const index = ids?.indexOf(variables.jobId) ?? -1;
        return index >= 0 ? [{ queryKey, index }] : [];
      });
      const archiveMetadata =
        variables.operation === 'archive'
          ? { deletedAt: new Date().toISOString(), deletedBy: previousJob?.deletedBy ?? null }
          : { deletedAt: null, deletedBy: null };

      queryClient.setQueryData<Job | undefined>(getJobQueryKey(variables.jobId), (currentJob) =>
        currentJob ? { ...currentJob, ...archiveMetadata } : currentJob
      );
      jobQueries.forEach(([queryKey]) => {
        queryClient.setQueryData<JobResponse | undefined>(queryKey, (current) =>
          updateJobCollection(current, variables, archiveMetadata, getQueryArchiveState(queryKey))
        );
      });
      filteredIdsQueries.forEach(([queryKey]) => {
        queryClient.setQueryData<string[] | undefined>(queryKey, (current) =>
          isSourceArchiveScope(getQueryArchiveState(queryKey), variables.operation)
            ? current?.filter((jobId) => jobId !== variables.jobId)
            : current
        );
      });

      return { previousJob, jobCollectionSnapshots, filteredIdsSnapshots };
    },
    onError: (_error, { jobId }, context) => {
      if (context?.previousJob) {
        queryClient.setQueryData(getJobQueryKey(jobId), context.previousJob);
      }
      context?.jobCollectionSnapshots.forEach((snapshot) => {
        queryClient.setQueryData<JobResponse | undefined>(snapshot.queryKey, (current) =>
          restoreJobCollection(current, snapshot)
        );
      });
      context?.filteredIdsSnapshots.forEach((snapshot) => {
        queryClient.setQueryData<string[] | undefined>(snapshot.queryKey, (current) =>
          restoreFilteredIds(current, snapshot, jobId)
        );
      });
    },
    onSuccess: ({ job }, { jobId }) => {
      queryClient.setQueryData(getJobQueryKey(jobId), job);
      queryClient.setQueriesData<JobResponse>(
        {
          predicate: ({ queryKey }) => queryKey[0] === jobsQueryKey && queryBelongsToLab(queryKey, labId),
        },
        (current) =>
          current
            ? { ...current, jobs: current.jobs.map((cachedJob) => (cachedJob.id === jobId ? job : cachedJob)) }
            : current
      );
    },
    onSettled: (response, _error, { jobId }, context) => {
      void queryClient.invalidateQueries({ queryKey: getJobQueryKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: [jobExperimentResultSummariesQueryKey, labId] });
      void queryClient.invalidateQueries({ queryKey: [jobExperimentResultDetailQueryKey, labId, jobId] });

      const hasOtherPendingJob = queryClient
        .getMutationCache()
        .findAll({ mutationKey: jobArchiveMutationKey })
        .some(
          (pendingMutation) =>
            pendingMutation.state.status === 'loading' &&
            (pendingMutation.state.variables as unknown as JobArchiveMutationVariables | undefined)?.jobId !== jobId
        );
      if (!hasOtherPendingJob) {
        void queryClient.invalidateQueries({
          predicate: ({ queryKey }) => queryKey[0] === jobsQueryKey && queryBelongsToLab(queryKey, labId),
        });
        void queryClient.invalidateQueries({
          predicate: ({ queryKey }) => queryKey[0] === jobsFilteredIdsQueryKey && queryBelongsToLab(queryKey, labId),
        });
      }

      const job = response?.job ?? context?.previousJob;
      if (job && isIngestFamilyJob(job) && job.studyId) {
        void invalidateSlideVisibility(queryClient, job.studyId);
      }
    },
  });

  return {
    ...mutation,
    isPendingForJob: (jobId: string) => Boolean(getPendingOperation(queryClient, jobId)),
  };
};
